import { computed, onScopeDispose, ref, watch } from 'vue';
import { ReservationsService } from '@/api/services/reservations.service';
import { toDateInputValue } from '@/domain/stats/stats-date-range';
import type { IStatsDateRange } from '@/domain/stats/stats-date-range';
import type { IReservationStats } from '@/interfaces/IReservationStats';
import { useRestaurantStore } from '@/stores/restaurant.store';

export function useReservationReports() {
  const restaurant = useRestaurantStore();
  const range = ref<IStatsDateRange | null>(null);
  const allBranches = ref(false);
  const stats = ref<IReservationStats | null>(null);
  const loading = ref(false);
  const error = ref(false);
  let alive = true;
  let sequence = 0;
  const scope = computed(
    () =>
      `${restaurant.restaurant?.id}:${restaurant.selectedBranch?.id}:${allBranches.value}`
  );
  const rangeKey = computed(() =>
    range.value
      ? `${toDateInputValue(range.value.from)}:${toDateInputValue(
          range.value.to
        )}`
      : ''
  );
  async function load() {
    const request = ++sequence;
    const key = `${scope.value}:${rangeKey.value}`;
    const current = () =>
      alive &&
      request === sequence &&
      key === `${scope.value}:${rangeKey.value}`;
    const r = restaurant.restaurant?.id;
    const b = allBranches.value ? null : restaurant.selectedBranch?.id;
    if (!r || (!allBranches.value && !b) || !range.value) {
      stats.value = null;
      loading.value = false;
      return;
    }
    loading.value = true;
    error.value = false;
    stats.value = null;
    try {
      const result = await ReservationsService.getStats(
        r,
        b ?? null,
        toDateInputValue(range.value.from),
        toDateInputValue(range.value.to)
      );
      if (current()) stats.value = result;
    } catch {
      if (current()) error.value = true;
    } finally {
      if (current()) loading.value = false;
    }
  }
  watch([scope, rangeKey], () => {
    void load();
  });
  onScopeDispose(() => {
    alive = false;
    ++sequence;
  });
  return { range, allBranches, stats, loading, error, load };
}
